// @flow
import React from 'react'
import styled, { type StyledComponent } from 'styled-components'
import type { Configs, Crop, Media } from '../../types'
import MediaLoaderContainer from '../MediaLoaderContainer'
import { getImageSizeRestrictionsFromSingleConfig } from '../../AdEdit'
import PreviewImageCropper from '../PreviewImageCropper'
import type { CarouselCard } from '../../../redux/ducks/carouselEdit/reducers'
import LineInput from '../AdInputs/LineInput'
import LabeledInput from '../AdInputs/LabeledInput'
import { grayColor } from '../../controls/colors'
import LinkButtons from '../AdInputs/LinkButtons'
import LimitedInput from '../AdInputs/LimitedInput'

const Wrapper: StyledComponent<Object, any, any> = styled.div`
  padding: 5px;
  border: solid 1px ${grayColor};
  width: 675px;
`
const MediaWrapper: StyledComponent<Object, any, any> = styled.div`
  display: flex;
`
const Row: StyledComponent<Object, any, any> = styled.div`
  padding-bottom: 14px;
  white-space: nowrap;
`
const Spacer: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  width: 5px;
`

export type CardControlVisibility = {|
  description: boolean,
  vkLinkButton: boolean,
  price: boolean,
  oldPrice: boolean
|}

type Props = {|
  card: CarouselCard,
  index: number,
  allowMultiUpload: boolean,
  image: Media,
  configs: Configs,
  onUrlChange: (string) => void,
  onTitleChange: (string) => void,
  onDescriptionChange: (string) => void,
  onImageChange: (string) => void,
  onCropChange: (Crop) => void,
  onVkLinkButtonChange: (string) => void,
  onPriceChange: (string) => void,
  onOldPriceChange: (string) => void,
  cardControlVisibility: CardControlVisibility
|}

const t = {
  url: 'Ссылка для карточки',
  title: 'Заголовок для карточки',
  description: 'Описание для карточки',
  image: 'Картинка для карточки',
  no_button: '(без кнопки)',
  price: 'Цена товара (0 = "Бесплатно")',
  old_price: 'Старая цена товара'
}

export default function Card(props: Props) {
  const card = props.card
  const imageConfigs = props.configs.carouselImageConfigs

  return (
    <Wrapper>
      <div>
        <Row>
          <LabeledInput
            className="t-CarouselCard__Url"
            header={`${t.url} #${props.index + 1}`}
            input={<LineInput value={card.url} onChange={props.onUrlChange} />}
            errors={card.urlErrors}
          />
        </Row>
        <Row>
          <LabeledInput
            className="t-CarouselCard__Title"
            header={`${t.title} #${props.index + 1}`}
            input={
              <LimitedInput
                maxLength={props.configs.carouselCardTitleMaxLength}
                value={card.title}
                onChange={props.onTitleChange}
              />
            }
            errors={card.titleErrors}
          />
        </Row>
        {props.cardControlVisibility.description && (
          <Row>
            <LabeledInput
              className="t-CarouselCard__Description"
              header={`${t.description} #${props.index + 1}`}
              input={
                <LimitedInput
                  maxLength={props.configs.carouselCardDescriptionMaxLength}
                  value={card.description}
                  onChange={props.onDescriptionChange}
                />
              }
              errors={card.descriptionErrors}
            />
          </Row>
        )}
      </div>
      <Row>
        <LabeledInput
          className="t-CarouselCard__Image"
          header={`${t.image} #${props.index + 1}`}
          input={
            <MediaWrapper>
              <MediaLoaderContainer
                storeId={card.imageStoreId}
                imageSizeRestrictions={getImageSizeRestrictionsFromSingleConfig(imageConfigs)}
                errors={[]}
                height={150}
                onLoad={(storeIds: Array<string>) => props.onImageChange(storeIds[0])}
                mediaType="image"
                multiple={props.allowMultiUpload}
                maxImageSize={6}
              />
              <Spacer />
              {props.image && card.crop && (
                <PreviewImageCropper
                  title={null}
                  image={props.image}
                  crop={card.crop}
                  config={imageConfigs}
                  height={100}
                  onChange={props.onCropChange}
                />
              )}
            </MediaWrapper>
          }
          errors={card.imageErrors}
        />
      </Row>
      {props.cardControlVisibility.price && (
        <Row>
          <LabeledInput
            className="t-CarouselCard__Price"
            header={t.price}
            input={<LineInput value={card.price} onChange={props.onPriceChange} />}
            errors={card.priceErrors}
          />
        </Row>
      )}
      {props.cardControlVisibility.oldPrice && (
        <Row>
          <LabeledInput
            className="t-CarouselCard__OldPrice"
            header={t.old_price}
            input={<LineInput value={card.oldPrice} onChange={props.onOldPriceChange} />}
            errors={card.oldPriceErrors}
          />
        </Row>
      )}
      <LinkButtons
        vkOptions={[{ id: '', name: t.no_button }, ...props.configs.vkCarouselCardButtonOptions]}
        tmOptions={[]}
        fbOptions={[]}
        vkValue={card.vkLinkButton}
        tmValue={null}
        fbValue={null}
        onVkChange={props.onVkLinkButtonChange}
        onTmChange={() => {}}
        onFbChange={() => {}}
        vkErrors={card.vkLinkButtonErrors}
        hideVk={!props.cardControlVisibility.vkLinkButton}
        hideTm
        hideFb
      />
    </Wrapper>
  )
}
